import { IIdentity } from "@/customTypings/user/Identity";
import { useAtom } from "@dbeining/react-atom";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik } from "formik";
import React, { useState } from "react";
import {
    Alert,
    Button, Col, Form, InputGroup, Modal, Row, Stack
} from "react-bootstrap";
import * as Yup from "yup";
import { userAtom } from "../../../../atoms";
import { IPasswordComplexityResult } from "../../../../customTypings/auth/PasswordComplexityResult";
import { accountService } from "../../../../services";
import { Avatar } from "../../../common/Avatar/Avatar";
import { useFormikSubmitEffect } from "../../../forms/FormikSubmitEffect";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import { PasswordStrength } from "../../../forms/PasswordStrength";
import { showToastSuccess, showToastError } from "../../../../utils";

type Props = {
    show: boolean;
    user: IIdentity;
    setShow: React.Dispatch<React.SetStateAction<any>>;
    setUserInteraction?: React.Dispatch<React.SetStateAction<any>>;
};

const UserPasswordEdit: React.FC<Props> = ({
    show,
    user,
    setShow,
    setUserInteraction,
}) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [passwordComplexity, setPasswordComplexity] =
        useState<IPasswordComplexityResult>({
            isSuccess: false,
            score: 0,
            messages: [],
        });
    const currentUser = useAtom(userAtom);
    const { FormikSubmitEffect, hasSubmitted } = useFormikSubmitEffect();

    const handleClose = () => {
        setShow(false);
        setPasswordComplexity({ isSuccess: false, score: 0, messages: [] });
    };

    function validatePassword(value: string) {
        if (value === "") {
            setPasswordComplexity({ isSuccess: false, score: 0, messages: [] });
        } else {
            accountService
                .passwordComplexityCheck(
                    currentUser?.identity.email ?? "",
                    value
                )
                .then(
                    (result: IPasswordComplexityResult) => {
                        setPasswordComplexity(result);
                    },
                    (error: any) => {
                        setPasswordComplexity({
                            isSuccess: false,
                            score: 0,
                            messages: [error],
                        });
                    }
                );
        }
    }

    return (
        <Modal
            centered
            show={show}
            keyboard={false}
            onHide={handleClose}
            size="lg"
        >
            <Formik
                validateOnChange={hasSubmitted}
                validateOnBlur={hasSubmitted}
                initialValues={{
                    currentPassword: "",
                    showCurrentPassword: false,
                    newPassword: "",
                    showNewPassword: false,
                    confirmPassword: "",
                    showConfirmPassword: false,
                }}
                validationSchema={Yup.object().shape({
                    currentPassword: Yup.string().required(
                        "Current Password is required"
                    ),
                    newPassword: Yup.string().required(
                        "New Password is required"
                    ),
                    confirmPassword: Yup.string().required(
                        "Confirm Password is required"
                    ),
                })}
                onSubmit={(
                    values,
                    { setStatus, setSubmitting, setFieldError }
                ) => {
                    setStatus();
                    accountService
                        .updatePassword(
                            values.currentPassword,
                            values.newPassword,
                            values.confirmPassword
                        )
                        .then(
                            (response: any) => {
                                setSubmitting(false);
                                if (response.status !== "Failure") {
                                    if (setUserInteraction !== undefined) {
                                        setUserInteraction(true);
                                    }
                                    showToastSuccess("User Password updated successfully!");
                                    setTimeout(() => handleClose(), 1000);
                                } else {
                                    setAlertVariant("danger");
                                    showToastError(response.message);
                                }
                            },
                            (error: any) => {
                                setAlertVariant("danger");
                                if (error.status === 400 && error.errors) {
                                    Object.keys(error.errors).forEach((key) => {
                                        const messages = error.errors[key];
                                        if (Array.isArray(messages)) {
                                            messages.forEach((message) => showToastError(message));
                                        }
                                    });
                                } else {
                                    showToastError("An unexpected error occurred!");
                                }
                                setSubmitting(false);
                            }
                        );
                }}
            >
                {({
                    values,
                    handleChange,
                    errors,
                    touched,
                    status,
                    isSubmitting,
                    submitCount,
                    handleSubmit,
                    setFieldValue,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <FormikSubmitEffect submitCount={submitCount} />
                        <Modal.Header closeButton>
                            <Modal.Title>Change Password</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <h5 className="mb-3">
                                <Avatar identity={user} />
                            </h5>

                            <Stack gap={3}>
                                <Form.Group
                                    as={Row}
                                    className="mb-3"
                                    controlId="currentPassword"
                                >
                                    <Form.Label column sm={3}>
                                        Current Password <span style={{ color: 'red' }}>*</span>
                                    </Form.Label>
                                    <Col sm={9}>
                                        <InputGroup hasValidation>
                                            <Form.Control
                                                type={
                                                    values.showCurrentPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                name="currentPassword"
                                                value={values.currentPassword}
                                                onChange={handleChange}
                                                isInvalid={
                                                    !!errors.currentPassword
                                                }
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() =>
                                                    setFieldValue(
                                                        "showCurrentPassword",
                                                        !values.showCurrentPassword
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        values.showCurrentPassword
                                                            ? faEyeSlash
                                                            : faEye
                                                    }
                                                />
                                            </Button>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.currentPassword}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                <Form.Group
                                    as={Row}
                                    className="mb-3"
                                    controlId="newPassword"
                                >
                                    <Form.Label column sm={3}>
                                        New Password <span style={{ color: 'red' }}>*</span>
                                    </Form.Label>
                                    <Col sm={9}>
                                        <InputGroup hasValidation>
                                            <Form.Control
                                                type={
                                                    values.showNewPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                name="newPassword"
                                                value={values.newPassword}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    validatePassword(
                                                        e.target.value
                                                    );
                                                }}
                                                isInvalid={!!errors.newPassword}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() =>
                                                    setFieldValue(
                                                        "showNewPassword",
                                                        !values.showNewPassword
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        values.showNewPassword
                                                            ? faEyeSlash
                                                            : faEye
                                                    }
                                                />
                                            </Button>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.newPassword}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                {passwordComplexity && (
                                    <PasswordStrength
                                        passwordComplexityResult={
                                            passwordComplexity
                                        }
                                    />
                                )}
                                <Form.Group
                                    as={Row}
                                    className="mb-3"
                                    controlId="confirmPassword"
                                >
                                    <Form.Label column sm={3}>
                                        Confirm Password <span style={{ color: 'red' }}>*</span>
                                    </Form.Label>
                                    <Col sm={9}>
                                        <InputGroup hasValidation>
                                            <Form.Control
                                                type={
                                                    values.showConfirmPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                name="confirmPassword"
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                isInvalid={
                                                    !!errors.confirmPassword
                                                }
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() =>
                                                    setFieldValue(
                                                        "showConfirmPassword",
                                                        !values.showConfirmPassword
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        values.showConfirmPassword
                                                            ? faEyeSlash
                                                            : faEye
                                                    }
                                                />
                                            </Button>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.confirmPassword}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                {status && (
                                    <Alert variant={alertVariant}>
                                        {status}
                                    </Alert>
                                )}
                            </Stack>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="form-group">
                                <Button
                                    variant="primary"
                                    disabled={isSubmitting}
                                    type="submit"
                                    className="me-2"
                                >
                                    {isSubmitting ? (
                                        <LoadingSpinner text="Updating..." />
                                    ) : (
                                        "Update"
                                    )}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export { UserPasswordEdit };
